import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Pagination,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { Media } from "../types/Media";
import * as api from "../services/api";
import { config } from "../config/environment";
import { log, handleApiError } from "../utils/helpers";

const MediaList: React.FC = () => {
  const navigate = useNavigate();
  const [media, setMedia] = useState<Media[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<Media | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchMedia();
  }, [currentPage]);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/media?page=${currentPage}&limit=${itemsPerPage}`
      );
      setMedia(response.data.media);
      setTotalPages(response.data.totalPages);
      setTotalItems(response.data.totalItems);
    } catch (error) {
      console.error("Error fetching media:", error);
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedMedia) return;

    try {
      await api.deleteRequest(`/media/${selectedMedia.id}`);
      log(`Deleted media: ${selectedMedia.title}`);
      setDeleteDialogOpen(false);
      setSelectedMedia(null);
      fetchMedia();
    } catch (error) {
      console.error("Error deleting media:", error);
      handleApiError(error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Kelola Media
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/media/new")}
        >
          Tambah Media
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Gambar</TableCell>
              <TableCell>Judul</TableCell>
              <TableCell>Penulis</TableCell>
              <TableCell>Tanggal Publikasi</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Urutan</TableCell>
              <TableCell>Aksi</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {media.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <img
                    src={api.getImageUrl(item.image)}
                    alt={item.title}
                    style={{
                      width: 60,
                      height: 60,
                      objectFit: "cover",
                      borderRadius: 4,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    {truncateText(item.title, 50)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {truncateText(item.content.replace(/<[^>]*>/g, ""), 80)}
                  </Typography>
                </TableCell>
                <TableCell>{item.author}</TableCell>
                <TableCell>{formatDate(item.publishDate)}</TableCell>
                <TableCell>
                  <Chip
                    label={item.isPublished ? "Diterbitkan" : "Draft"}
                    color={item.isPublished ? "success" : "default"}
                    size="small"
                  />
                  {item.isFeatured && (
                    <Chip
                      label="Featured"
                      color="primary"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {item.order}
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => navigate(`/media/edit/${item.id}`)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => {
                      setSelectedMedia(item);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => setCurrentPage(page)}
            color="primary"
          />
        </Box>
      )}

      <Box mt={2}>
        <Typography variant="body2" color="text.secondary">
          Total: {totalItems} media
        </Typography>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Hapus Media</DialogTitle>
        <DialogContent>
          <Typography>
            Apakah Anda yakin ingin menghapus media "{selectedMedia?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Batal</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Hapus
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MediaList;
